import { useState } from 'react';
import { Button as AntButton, theme } from 'antd';
import { DeleteOutlined, FileTextOutlined } from '@ant-design/icons';
import type { MediaLink } from '@entities/media';

interface PersistedMediaItemProps {
  mediaId: string;
  /** Either the backend-resolved link or, for fresh uploads, just a URL we hold. */
  link?: MediaLink | null;
  fileName?: string;
  disabled?: boolean;
  onRemove: () => void;
}

const IMAGE_RE = /^image\//;

/**
 * View-row for a media file the form already references. Renders an inline
 * thumbnail (when the file is an image and a URL is known), otherwise a
 * neutral document icon. Click anywhere on the icon/name opens the file in a
 * new tab — backend's MediaLink already carries a ready-to-use URL, no
 * `/media/{id}/download` round-trip required.
 */
export function PersistedMediaItem({
  mediaId,
  link,
  fileName,
  disabled,
  onRemove,
}: PersistedMediaItemProps) {
  const { token } = theme.useToken();
  const [thumbBroken, setThumbBroken] = useState(false);

  const url = link?.url ?? null;
  const isImage = link?.contentType ? IMAGE_RE.test(link.contentType) : true;
  const displayName = fileName ?? `${mediaId.slice(0, 8)}…`;

  return (
    <li
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: 8,
        borderRadius: 8,
        border: `1px solid ${token.colorBorderSecondary}`,
        background: token.colorBgContainer,
      }}
    >
      <a
        href={url ?? '#'}
        target="_blank"
        rel="noreferrer"
        onClick={(e) => {
          if (!url) e.preventDefault();
        }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 44,
          height: 44,
          borderRadius: 6,
          background: token.colorFillTertiary,
          overflow: 'hidden',
          flexShrink: 0,
          color: token.colorTextSecondary,
          fontSize: 20,
        }}
        aria-label={`Open ${displayName}`}
      >
        {url && isImage && !thumbBroken ? (
          <img
            src={url}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={() => setThumbBroken(true)}
          />
        ) : (
          <FileTextOutlined />
        )}
      </a>
      <div style={{ flex: 1, minWidth: 0 }}>
        <a
          href={url ?? '#'}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => {
            if (!url) e.preventDefault();
          }}
          style={{
            display: 'block',
            fontSize: 13,
            fontWeight: 500,
            color: token.colorText,
            textDecoration: 'none',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {displayName}
        </a>
        <span
          style={{
            display: 'block',
            fontSize: 11,
            color: token.colorTextTertiary,
            fontFamily: 'ui-monospace, monospace',
          }}
        >
          {mediaId}
        </span>
      </div>
      <AntButton
        type="text"
        size="small"
        danger
        icon={<DeleteOutlined />}
        disabled={disabled}
        onClick={onRemove}
        aria-label="Remove file"
      />
    </li>
  );
}
